import React from 'react';
import { ShoppingCart, Truck, Package, Factory, Ship, IndianRupee, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, Badge, LOGO_URL, fmtInr, fmtNum } from '../ui';
import { HERO_IMG } from '../nav';
import { SAMPLE } from './sample';

export const HeroBanner = ({ user }) => (
  <div className="grid lg:grid-cols-[1fr_300px] gap-4 erp-rise">
    <Card className="relative overflow-hidden min-h-[210px]">
      <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover object-right" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, #fff 0%, #fff 34%, rgba(255,255,255,.55) 58%, rgba(255,255,255,0) 100%)' }} />
      <div className="relative h-full flex flex-col justify-center p-7 sm:p-9">
        <p className="text-lg font-medium" style={{ color: '#3b4664' }}>Welcome Back,</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-none mt-1" style={{ color: 'var(--erp-navy)' }} data-testid="hero-username">{user.name}</h1>
        <p className="mt-3 text-base font-medium" style={{ color: '#3b4664' }}>Together We Build a Stronger Tomorrow</p>
      </div>
      <div className="absolute right-8 bottom-7 hidden xl:block text-right">
        <p className="text-sm font-extrabold leading-6" style={{ color: 'var(--erp-navy)' }}>WE LISTEN<br />WE INNOVATE<br />WE GROW TOGETHER</p>
        <span className="inline-block h-1 w-14 rounded-full mt-2" style={{ background: 'var(--erp-navy)' }} />
      </div>
    </Card>
    <Card className="hidden lg:grid place-items-center p-6">
      <img src={LOGO_URL} alt="Namoh Toolings" className="max-h-40 object-contain" />
    </Card>
  </div>
);

const Delta = ({ v }) => (
  <span className={`inline-flex items-center gap-0.5 text-xs font-bold ${v >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
    {v >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}{Math.abs(v)}%
  </span>
);

const Kpi = ({ icon: Icon, label, value, foot, sample, delay, testid }) => (
  <Card hover className={`relative p-5 erp-rise ${delay}`} data-testid={testid}>
    {sample && <Badge tone="amber" className="absolute top-2.5 right-2.5 !h-5 !px-2 !text-[9.5px]">SAMPLE</Badge>}
    <div className="flex items-start gap-3.5">
      <span className="h-12 w-12 rounded-xl grid place-items-center text-white flex-shrink-0" style={{ background: 'linear-gradient(160deg, #2452c9, #0b1a4a)' }}><Icon size={22} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold leading-tight pr-12" style={{ color: 'var(--erp-muted)' }}>{label}</p>
        <p className="text-[26px] font-extrabold tracking-tight leading-tight mt-0.5" style={{ color: 'var(--erp-navy)' }}>{value}</p>
      </div>
    </div>
    <div className="mt-3 text-xs space-y-0.5" style={{ color: 'var(--erp-muted)' }}>{foot}</div>
  </Card>
);

const growth = (cur, prev) => (prev ? Math.round((cur - prev) * 100 / prev) : null);
const Foot = ({ cur, prev, fy }) => { const g = growth(cur, prev); return g === null ? <span>FY {fy} · no prior-year data</span> : <><Delta v={g} /> <span>vs FY {fy}</span></>; };

export const KpiRow = ({ data }) => {
  const s = SAMPLE.kpis;
  const live = data.live || {};
  const ar = data.ar || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      <Kpi icon={ShoppingCart} label="Sales (YTD)" value={fmtInr(data.sales_ytd)} delay="erp-d1" testid="kpi-sales"
        foot={<Foot cur={data.sales_ytd} prev={data.sales_prev_ytd} fy={data.prev_fy} />} />
      <Kpi icon={Truck} label="Purchase (YTD)" value={fmtInr(data.purchase_ytd)} delay="erp-d2" testid="kpi-purchase"
        foot={<Foot cur={data.purchase_ytd} prev={data.purchase_prev_ytd} fy={data.prev_fy} />} />
      <Kpi icon={Package} label="Inventory Value" value={fmtInr(data.stock_value)} delay="erp-d3" testid="kpi-inventory"
        foot={<><span className="font-bold" style={{ color: 'var(--erp-ink)' }}>{fmtNum(data.counts?.skus)}</span> Items · <span className="font-bold" style={{ color: 'var(--erp-ink)' }}>{fmtNum(data.counts?.branches)}</span> Locations</>} />
      <Kpi icon={Factory} label="Production (VCI)" value={`${fmtNum(s.production_mt.value)} MT`} sample={!live.production} delay="erp-d4" testid="kpi-production"
        foot={<><Delta v={s.production_mt.delta} /> <span>{s.production_mt.note}</span></>} />
      <Kpi icon={Ship} label="Import (Landed)" value={fmtInr(s.import_landed.value)} sample={!live.import} delay="erp-d5" testid="kpi-import"
        foot={<><span className="font-bold" style={{ color: 'var(--erp-ink)' }}>{s.import_landed.shipments}</span> Shipments<br />In Transit: <span className="font-bold" style={{ color: 'var(--erp-ink)' }}>{s.import_landed.in_transit}</span></>} />
      <Kpi icon={IndianRupee} label="Outstanding (AR)" value={fmtInr(ar.outstanding)} delay="erp-d6" testid="kpi-ar"
        foot={<><span className="font-bold text-red-600">Overdue: {fmtInr(ar.overdue)}</span><br />Open invoices: {fmtNum(ar.open_invoices)}</>} />
    </div>
  );
};
